/**
 * 見積り乖離判定に関するユーティリティ
 */
import { CellClassParams } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
} from '../productBacklog'

// 小数点誤差を考慮した閾値
const VARIANCE_THRESHOLD = 0.001

// スプリントごとの見積り工数情報
export type SprintEstimatedHourInfo = {
  sprintUuid: string
  sprintName: string
  estimatedHour: number
}

// 全スプリントの見積り時間合計を取得（sprintProductItemsベース）
const getTotalSprintEstimatedHour = (pbiRow: ProductBacklogItemRow): number => {
  const spis = pbiRow.sprintProductItems || []
  return spis.reduce((total, spi) => total + (spi.estimatedHour || 0), 0)
}

// スプリント別の見積り工数リストを取得（同一スプリントは合算）
const getSprintHourList = (
  pbiRow: ProductBacklogItemRow,
  hourExtractor: (
    spi: ProductBacklogItemRow['sprintProductItems'][number]
  ) => number
): SprintEstimatedHourInfo[] => {
  const spis = pbiRow.sprintProductItems || []
  const sprints = pbiRow.wbsItem.sprints || []

  // スプリントUUIDごとに工数を合算
  const hourBySprintUuid = new Map<string, number>()
  spis.forEach(spi => {
    const current = hourBySprintUuid.get(spi.sprintUuid) || 0
    hourBySprintUuid.set(spi.sprintUuid, current + hourExtractor(spi))
  })

  // スプリント情報を取得してリストを作成（重複排除済み）
  const result: SprintEstimatedHourInfo[] = []
  const processedSprintUuids = new Set<string>()

  sprints.forEach(sprint => {
    if (!sprint) return
    if (processedSprintUuids.has(sprint.uuid)) return
    processedSprintUuids.add(sprint.uuid)

    const totalHour = hourBySprintUuid.get(sprint.uuid) || 0
    result.push({
      sprintUuid: sprint.uuid,
      sprintName: sprint.name,
      estimatedHour: totalHour,
    })
  })

  return result
}

// スプリント別の成果物見積り工数リスト
export const getSprintEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] =>
  getSprintHourList(pbiRow, spi => spi.estimatedHour || 0)

// スプリント別のタスク見積り工数リスト
export const getSprintTaskEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] =>
  getSprintHourList(pbiRow, spi => spi.taskEstimatedHour || 0)

// 乖離情報を取得
export const getVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const originalHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const sprintTotalHour = getTotalSprintEstimatedHour(pbiRow)
  const hasVariance =
    Math.abs(originalHour - sprintTotalHour) > VARIANCE_THRESHOLD
  const sprintBreakdown = getSprintEstimatedHourList(pbiRow)
  return { originalHour, sprintTotalHour, hasVariance, sprintBreakdown }
}

// 乖離判定対象の行データを取得（対象外ならnull）
export const getVarianceTargetRow = (
  data: ProductBacklogRow | undefined
): ProductBacklogItemRow | null => {
  if (!data || !isProductBacklogItemRow(data)) return null
  const parentSprintUuid = data.treeValue?.[0]
  if (!parentSprintUuid) return null
  return data
}

// ============================================================================
// タスク見積り乖離判定（行単位: 成果物スプリント vs タスクスプリント）
// ============================================================================

// PBI行からSprintProductItemを検索する（行UUID優先、sprintUuidフォールバック）
export const findSprintProductItem = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string
) => {
  return (
    pbiRow.sprintProductItems?.find(spi => spi.uuid === pbiRow.uuid) ??
    pbiRow.sprintProductItems?.find(spi => spi.sprintUuid === sprintUuid)
  )
}

// 行のSprintProductItemを取得
const getRowSprintProductItem = (pbiRow: ProductBacklogItemRow) => {
  const parentSprintUuid = pbiRow.treeValue?.[0]
  if (!parentSprintUuid) return undefined
  return findSprintProductItem(pbiRow, parentSprintUuid)
}

// タスク乖離情報を取得（行単位の成果物スプリント vs タスクスプリント）
export const getTaskVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const spi = getRowSprintProductItem(pbiRow)
  const deliverableHour = spi?.estimatedHour || 0
  const taskHour = spi?.taskEstimatedHour || 0
  const hasVariance = Math.abs(deliverableHour - taskHour) > VARIANCE_THRESHOLD
  return { deliverableHour, taskHour, hasVariance }
}

// ============================================================================
// 成果物 vs タスク 乖離判定（合計: 成果物見積もり vs タスク見積もり合計）
// ============================================================================

// 成果物vsタスクの合計乖離情報を取得
export const getTotalTaskVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const deliverableHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const taskHour = pbiRow.cumulation
    ? pbiRow.cumulation.sumTaskEstimatedHour -
      pbiRow.cumulation.sumTaskEstimatedHourDiscard
    : 0
  const hasVariance = Math.abs(deliverableHour - taskHour) > VARIANCE_THRESHOLD
  return { deliverableHour, taskHour, hasVariance }
}

// ============================================================================
// 乖離背景色スタイル（共通ファクトリ）
// ============================================================================

const createVarianceCellStyle =
  (checkVariance: (row: ProductBacklogItemRow) => boolean) =>
  (
    params: CellClassParams<ProductBacklogRow>
  ): { background: string } | undefined => {
    const row = getVarianceTargetRow(params.data)
    if (!row) return undefined
    if (checkVariance(row)) return { background: colorPalette.pink[1] }
    return undefined
  }

// 成果物列用: 成果物vsΣスプリント or 成果物vsタスク合計
export const getDeliverableTotalVarianceCellStyle = createVarianceCellStyle(
  row =>
    getVarianceInfo(row).hasVariance ||
    getTotalTaskVarianceInfo(row).hasVariance
)
// 成果物（スプリント）列用: 成果物vsΣスプリント or スプリントvsタスクスプリント
export const getDeliverableSprintVarianceCellStyle = createVarianceCellStyle(
  row =>
    getVarianceInfo(row).hasVariance || getTaskVarianceInfo(row).hasVariance
)
// タスク列用: 成果物vsタスク合計
export const getTotalTaskVarianceCellStyle = createVarianceCellStyle(
  row => getTotalTaskVarianceInfo(row).hasVariance
)
// タスク（スプリント）列用: スプリントvsタスクスプリント
export const getTaskVarianceCellStyle = createVarianceCellStyle(
  row => getTaskVarianceInfo(row).hasVariance
)
